import React from "react";
import { clsx } from "@/utils";
import { Icon, Typography } from "../";

export interface SnackbarProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "default" | "small";
  textMode?: "single" | "multiple";
  title?: string;
  message: string;
  actionText?: string;
  onActionClick?: () => void;
  timestamp?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  visible?: boolean;
}

export const Snackbar = ({
  size = "default",
  textMode = "single",
  title,
  message,
  actionText,
  onActionClick,
  timestamp,
  icon,
  onClose,
  visible = true,
  className,
  ...props
}: SnackbarProps) => {
  const transition = {
    "animate-enter": visible,
    "animate-leave": !visible,
  };

  const renderHeader = () => {
    if (size === "small" || !title) return null;

    return (
      <div className="flex items-center justify-between border-b border-neutral-dark-gray p-3">
        <div className="flex items-center gap-2">
          {icon && <div className="flex h-5 w-5 shrink-0 items-center justify-center text-white">{icon}</div>}
          <Typography variant="title" className="text-white">
            {title}
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          {timestamp && (
            <Typography variant="small-regular" className="text-neutral-silver-gray">
              {timestamp}
            </Typography>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex h-5 w-5 items-center justify-center text-white transition-colors hover:text-gray-300"
            >
              <Icon name="close" isCustom size="md" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (textMode === "multiple") {
      return (
        <div className="flex flex-col items-end gap-2 p-3">
          <Typography variant="paragraph" className="w-full text-white">
            {message}
          </Typography>
          {actionText && (
            <button
              type="button"
              onClick={onActionClick}
              className="text-base font-semibold text-info transition-colors hover:text-info-light"
            >
              {actionText}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-3">
        <Typography variant="paragraph" className="text-white">
          {message}
        </Typography>
        {actionText && (
          <button
            type="button"
            onClick={onActionClick}
            className="text-base font-semibold text-info transition-colors hover:text-info-light"
          >
            {actionText}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={clsx("w-[344px] overflow-hidden rounded-lg bg-neutral-black shadow-lg", transition, className)} {...props}>
      {renderHeader()}
      {renderBody()}
    </div>
  );
};
